import { createContext, useContext, type ReactNode } from 'react';
import type { UploadedDocument } from './types';

export interface FormUploadOptions {
  /**
   * Pulled from the picker field's metadata (`field.document_type_id`).
   * Required by the versioned document API — pickers reject uploads without it.
   */
  documentTypeId?: number;
  /** Abort handle so the field can cancel inflight uploads on remove/unmount. */
  signal?: AbortSignal;
}

/**
 * Upload contract used by `versioned_image_picker` / `versioned_pdf_picker`.
 * The function uploads one file and returns the new document version's ids.
 * Multi-file pickers invoke this once per file in parallel.
 */
export type FormUploadFn = (file: File, options?: FormUploadOptions) => Promise<UploadedDocument>;

interface FormUploadValue {
  uploadFile: FormUploadFn;
}

const FormUploadContext = createContext<FormUploadValue | null>(null);

export const FormUploadProvider = ({
  uploadFile,
  children,
}: {
  uploadFile: FormUploadFn;
  children: ReactNode;
}) => {
  return <FormUploadContext.Provider value={{ uploadFile }}>{children}</FormUploadContext.Provider>;
};

/**
 * Returns the configured upload function or `null` when no provider wraps the
 * tree. File-picker widgets surface a clear "uploads disabled" UI in that
 * case rather than failing silently.
 */
export const useFormUpload = (): FormUploadFn | null => {
  const ctx = useContext(FormUploadContext);
  return ctx ? ctx.uploadFile : null;
};
